import React, { useState, useEffect } from 'react';
import { InitiatoryInstructionItem, InitiatoryInstructionItemFormData, Employee } from '../../types/models';
import { InitiatoryInstructionItemService } from '../../services/initiatoryInstructionItemService';
import { EmployeeService } from '../../services/employeeService';
import { EmployeeAutocomplete } from '../EmployeeAutocomplete';
import { formatDateForInput } from '../../utils/dateUtils';

interface InitiatoryInstructionItemFormProps {
  instruction?: InitiatoryInstructionItem;
  employeeId?: number;
  onSuccess: (createdItem?: InitiatoryInstructionItem) => void;
  onCancel: () => void;
}

export const InitiatoryInstructionItemForm: React.FC<InitiatoryInstructionItemFormProps> = ({
  instruction,
  employeeId,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<InitiatoryInstructionItemFormData>({
    employeeId: instruction?.employeeId || employeeId || 0,
    responsibleEmployeeId: instruction?.responsibleEmployeeId || 0,
    date: instruction?.date ? new Date(instruction.date).toISOString().split('T')[0] : '',
  });

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Загружаем всех сотрудников
      const employeesResponse = await EmployeeService.getAll();

      if (employeesResponse.success) {
        setEmployees(employeesResponse.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let response;
      if (instruction) {
        // Обновление
        response = await InitiatoryInstructionItemService.update(instruction.id, { ...formData, id: instruction.id });
      } else {
        // Создание
        response = await InitiatoryInstructionItemService.create(formData);
      }

      if (response.success) {
        // Для создания передаем созданный элемент, для обновления - ничего
        onSuccess(instruction ? undefined : (response.data as InitiatoryInstructionItem));
      } else {
        setError(response.message || 'Ошибка сохранения');
      }
    } catch (err) {
      setError('Произошла ошибка при сохранении');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('Id') ? Number(value) : value
    }));
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{ maxWidth: '800px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card" style={{ margin: 0, boxShadow: 'none' }}>
          <h3>{instruction ? 'Редактирование вводного инструктажа' : 'Создание вводного инструктажа'}</h3>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Сотрудник *</label>
            <EmployeeAutocomplete
              value={formData.employeeId}
              onChange={(employeeId) => setFormData(prev => ({ ...prev, employeeId: employeeId || 0 }))}
              placeholder="Начните вводить ФИО сотрудника..."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Ответственный сотрудник *</label>
            <EmployeeAutocomplete
              value={formData.responsibleEmployeeId}
              onChange={(employeeId) => setFormData(prev => ({ ...prev, responsibleEmployeeId: employeeId || 0 }))}
              placeholder="Начните вводить ФИО ответственного..."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Дата проведения *</label>
            <input
              type="date"
              name="date"
              className="form-input"
              value={formatDateForInput(formData.date)}
              onChange={handleChange}
              required
            />
          </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Сохранение...' : (instruction ? 'Сохранить' : 'Создать')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Отмена
          </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};
